// ---------------------------------------------------------------------------
// Decals — Pooled DecalGeometry system with a ring buffer (~50 max).
// Handles both static map decals (graffiti, stains, cracks) and runtime
// decals (bullet impacts, blood).
// ---------------------------------------------------------------------------

import * as THREE from 'three';
import { DecalGeometry } from 'three/examples/jsm/geometries/DecalGeometry.js';
import type { DecalDef } from './map-format';
import { EFFECT_LAYER } from '../renderer/enhanced-effects';

// ---------------------------------------------------------------------------
// Decal pool (ring buffer)
// ---------------------------------------------------------------------------

const MAX_DECALS = 150;
const decalPool: Array<{ mesh: THREE.Mesh; age: number }> = [];
let decalIndex = 0;
let decalScene: THREE.Scene | null = null;

// ---------------------------------------------------------------------------
// Permanent decal system (map-placed, not subject to ring buffer)
// ---------------------------------------------------------------------------

const permanentDecals: THREE.Mesh[] = [];

/** Initialize the decal system with a scene reference. */
export function initDecals(scene: THREE.Scene): void {
  decalScene = scene;
}

// ---------------------------------------------------------------------------
// Decal textures (procedural canvas)
// ---------------------------------------------------------------------------

const decalTextureCache = new Map<string, THREE.Texture>();

function getDecalTexture(type: string): THREE.Texture {
  const cached = decalTextureCache.get(type);
  if (cached) return cached;

  const tex = generateDecalTexture(type);
  decalTextureCache.set(type, tex);
  return tex;
}

function generateDecalTexture(type: string): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cx = size / 2;
  const cy = size / 2;

  switch (type) {
    case 'bullet_hole': {
      // Dark center hole with cracks
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, cx * 0.8);
      grad.addColorStop(0, 'rgba(15,12,10,0.95)');
      grad.addColorStop(0.2, 'rgba(30,25,20,0.85)');
      grad.addColorStop(0.4, 'rgba(60,55,45,0.4)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);

      // Crack lines
      ctx.strokeStyle = 'rgba(20,18,15,0.5)';
      ctx.lineWidth = 1;
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI * 2 * i) / 6 + (Math.random() - 0.5) * 0.4;
        const len = 15 + Math.random() * 25;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(angle) * 5, cy + Math.sin(angle) * 5);
        ctx.lineTo(
          cx + Math.cos(angle) * len + (Math.random() - 0.5) * 5,
          cy + Math.sin(angle) * len + (Math.random() - 0.5) * 5,
        );
        ctx.stroke();
      }
      break;
    }

    case 'blood': {
      // Blood splatter
      ctx.fillStyle = 'rgba(120,10,10,0)';
      ctx.fillRect(0, 0, size, size);
      for (let i = 0; i < 12; i++) {
        const x = cx + (Math.random() - 0.5) * size * 0.6;
        const y = cy + (Math.random() - 0.5) * size * 0.6;
        const r = 5 + Math.random() * 20;
        const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
        grad.addColorStop(0, `rgba(${100 + Math.random() * 40},5,5,0.7)`);
        grad.addColorStop(1, 'rgba(80,0,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }

    case 'scorch': {
      // Explosion scorch mark
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, cx);
      grad.addColorStop(0, 'rgba(10,10,10,0.8)');
      grad.addColorStop(0.3, 'rgba(30,25,15,0.6)');
      grad.addColorStop(0.6, 'rgba(50,40,20,0.3)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);
      break;
    }

    case 'crack': {
      // Wall crack
      ctx.strokeStyle = 'rgba(30,25,20,0.6)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      let px = cx, py = 10;
      ctx.moveTo(px, py);
      for (let i = 0; i < 10; i++) {
        px += (Math.random() - 0.5) * 25;
        py += 10 + Math.random() * 8;
        ctx.lineTo(px, py);
        // Branch cracks
        if (Math.random() > 0.6) {
          ctx.moveTo(px, py);
          ctx.lineTo(px + (Math.random() - 0.5) * 20, py + Math.random() * 15);
          ctx.moveTo(px, py);
        }
      }
      ctx.stroke();
      break;
    }

    case 'stain': {
      // Water/oil stain
      for (let i = 0; i < 5; i++) {
        const x = cx + (Math.random() - 0.5) * 40;
        const y = cy + (Math.random() - 0.5) * 40;
        const r = 10 + Math.random() * 30;
        const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
        grad.addColorStop(0, 'rgba(60,50,40,0.25)');
        grad.addColorStop(1, 'rgba(60,50,40,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }

    case 'graffiti': {
      // Abstract graffiti mark
      const colors = ['#e63946', '#457b9d', '#2a9d8f', '#e9c46a', '#f4a261'];
      const color = colors[Math.floor(Math.random() * colors.length)];
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.beginPath();
      let gx = 20 + Math.random() * 20;
      let gy = 20 + Math.random() * 20;
      ctx.moveTo(gx, gy);
      for (let i = 0; i < 8; i++) {
        gx += (Math.random() - 0.3) * 30;
        gy += (Math.random() - 0.3) * 30;
        ctx.lineTo(gx, gy);
      }
      ctx.stroke();
      // Spray dots
      ctx.fillStyle = color;
      for (let i = 0; i < 20; i++) {
        ctx.globalAlpha = 0.3 + Math.random() * 0.4;
        ctx.beginPath();
        ctx.arc(
          Math.random() * size, Math.random() * size,
          1 + Math.random() * 2, 0, Math.PI * 2,
        );
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      break;
    }

    case 'ground_marking': {
      // Faded painted text/number on ground
      ctx.fillStyle = 'rgba(220,210,180,0.0)';
      ctx.fillRect(0, 0, size, size);
      ctx.strokeStyle = 'rgba(200,190,150,0.55)';
      ctx.lineWidth = 8;
      ctx.lineCap = 'square';
      // Simplified block numeral "4"
      ctx.beginPath();
      ctx.moveTo(cx - 20, cy - 40);
      ctx.lineTo(cx - 20, cy);
      ctx.lineTo(cx + 25, cy);
      ctx.moveTo(cx + 25, cy - 40);
      ctx.lineTo(cx + 25, cy + 40);
      ctx.stroke();
      // Fade edges with alpha gradient
      const fade = ctx.createRadialGradient(cx, cy, size * 0.3, cx, cy, cx);
      fade.addColorStop(0, 'rgba(0,0,0,0)');
      fade.addColorStop(1, 'rgba(0,0,0,0.6)');
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = fade;
      ctx.fillRect(0, 0, size, size);
      ctx.globalCompositeOperation = 'source-over';
      break;
    }

    case 'oil_stain': {
      // Iridescent oil puddle
      for (let i = 0; i < 4; i++) {
        const ox = cx + (Math.random() - 0.5) * 40;
        const oy = cy + (Math.random() - 0.5) * 40;
        const r = 20 + Math.random() * 40;
        const grad = ctx.createRadialGradient(ox, oy, 0, ox, oy, r);
        const hue = Math.floor(Math.random() * 360);
        grad.addColorStop(0, `hsla(${hue},60%,40%,0.4)`);
        grad.addColorStop(0.5, `hsla(${(hue + 60) % 360},50%,35%,0.25)`);
        grad.addColorStop(1, 'rgba(20,18,15,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(ox, oy, r, r * 0.6, Math.random() * Math.PI, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }

    case 'sign': {
      // Faded painted sign — white rectangle with dark border
      ctx.fillStyle = 'rgba(200,195,170,0.6)';
      ctx.fillRect(cx - 50, cy - 30, 100, 60);
      ctx.strokeStyle = 'rgba(80,70,60,0.5)';
      ctx.lineWidth = 4;
      ctx.strokeRect(cx - 50, cy - 30, 100, 60);
      // Horizontal text lines suggestion
      ctx.fillStyle = 'rgba(60,55,50,0.4)';
      for (let i = 0; i < 3; i++) {
        ctx.fillRect(cx - 35, cy - 16 + i * 14, 70, 5);
      }
      // Fade edges
      const sFade = ctx.createRadialGradient(cx, cy, size * 0.25, cx, cy, cx);
      sFade.addColorStop(0, 'rgba(0,0,0,0)');
      sFade.addColorStop(1, 'rgba(0,0,0,0.7)');
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = sFade;
      ctx.fillRect(0, 0, size, size);
      ctx.globalCompositeOperation = 'source-over';
      break;
    }

    case 'wall_crack': {
      // Wider surface crack with shadow depth
      ctx.strokeStyle = 'rgba(15,12,10,0.7)';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const drawCrack = (startX: number, startY: number, len: number, angle: number, depth: number) => {
        if (depth <= 0 || len < 4) return;
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        const ex = startX + Math.cos(angle) * len;
        const ey = startY + Math.sin(angle) * len;
        ctx.lineTo(ex, ey);
        ctx.lineWidth = depth * 1.5;
        ctx.stroke();
        if (Math.random() > 0.5) {
          drawCrack(ex, ey, len * 0.5, angle + (Math.random() - 0.5) * 1.2, depth - 1);
        }
        drawCrack(ex, ey, len * 0.7, angle + (Math.random() - 0.5) * 0.5, depth - 1);
      };
      drawCrack(cx, 20, 60, Math.PI / 2, 3);
      break;
    }

    case 'moss_patch': {
      // Green organic moss blob
      for (let i = 0; i < 25; i++) {
        const mx = cx + (Math.random() - 0.5) * size * 0.7;
        const my = cy + (Math.random() - 0.5) * size * 0.7;
        const r = 4 + Math.random() * 18;
        const green = 80 + Math.floor(Math.random() * 60);
        const grad = ctx.createRadialGradient(mx, my, 0, mx, my, r);
        grad.addColorStop(0, `rgba(30,${green},20,0.55)`);
        grad.addColorStop(1, `rgba(15,${green - 20},10,0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(mx, my, r, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }

    case 'water_stain': {
      // Tide-mark water stain (ring-style)
      for (let ring = 0; ring < 4; ring++) {
        const r = 30 + ring * 15;
        const grad = ctx.createRadialGradient(cx, cy, r - 5, cx, cy, r + 3);
        grad.addColorStop(0, 'rgba(150,140,110,0)');
        grad.addColorStop(0.5, `rgba(150,140,110,${0.18 - ring * 0.03})`);
        grad.addColorStop(1, 'rgba(150,140,110,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
      }
      // Streak drips
      ctx.strokeStyle = 'rgba(130,120,90,0.15)';
      ctx.lineWidth = 2;
      for (let i = 0; i < 5; i++) {
        const sx = cx + (Math.random() - 0.5) * 60;
        ctx.beginPath();
        ctx.moveTo(sx, cy + 40);
        ctx.lineTo(sx + (Math.random() - 0.5) * 10, cy + 55 + Math.random() * 30);
        ctx.stroke();
      }
      break;
    }

    case 'parking_stripe': {
      // White parking lot line / zone stripe
      ctx.fillStyle = 'rgba(220,215,200,0.7)';
      // Main stripe
      ctx.fillRect(cx - 8, 5, 16, size - 10);
      // Wear pattern (remove some paint)
      ctx.globalCompositeOperation = 'destination-out';
      for (let i = 0; i < 12; i++) {
        const wx = cx - 8 + Math.random() * 16;
        const wy = 10 + Math.random() * (size - 20);
        ctx.fillStyle = `rgba(0,0,0,${0.1 + Math.random() * 0.3})`;
        ctx.fillRect(wx, wy, 3 + Math.random() * 8, 2 + Math.random() * 5);
      }
      ctx.globalCompositeOperation = 'source-over';
      // Edge fade
      const pFade = ctx.createLinearGradient(0, 0, size, 0);
      pFade.addColorStop(0, 'rgba(0,0,0,0.7)');
      pFade.addColorStop(0.2, 'rgba(0,0,0,0)');
      pFade.addColorStop(0.8, 'rgba(0,0,0,0)');
      pFade.addColorStop(1, 'rgba(0,0,0,0.7)');
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = pFade;
      ctx.fillRect(0, 0, size, size);
      ctx.globalCompositeOperation = 'source-over';
      break;
    }

    case 'arrow_marking': {
      // Ground arrow direction marking
      ctx.fillStyle = 'rgba(220,210,170,0.6)';
      ctx.beginPath();
      // Arrow shaft
      ctx.rect(cx - 10, cy + 10, 20, 50);
      // Arrow head
      ctx.moveTo(cx, cy - 20);
      ctx.lineTo(cx - 28, cy + 18);
      ctx.lineTo(cx + 28, cy + 18);
      ctx.closePath();
      ctx.fill();
      // Wear-out overlay
      const aFade = ctx.createRadialGradient(cx, cy, size * 0.15, cx, cy, cx);
      aFade.addColorStop(0, 'rgba(0,0,0,0)');
      aFade.addColorStop(1, 'rgba(0,0,0,0.6)');
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = aFade;
      ctx.fillRect(0, 0, size, size);
      ctx.globalCompositeOperation = 'source-over';
      break;
    }

    case 'rust_drip': {
      // Orange-brown rust drip streaks
      for (let i = 0; i < 6; i++) {
        const rx = 20 + Math.random() * (size - 40);
        const rLen = 30 + Math.random() * 60;
        const rWidth = 2 + Math.random() * 5;
        const grad = ctx.createLinearGradient(rx, 10, rx + (Math.random() - 0.5) * 10, 10 + rLen);
        grad.addColorStop(0, `rgba(160,80,10,${0.5 + Math.random() * 0.3})`);
        grad.addColorStop(0.7, `rgba(130,60,5,0.3)`);
        grad.addColorStop(1, 'rgba(100,40,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(rx, 10 + rLen * 0.5, rWidth, rLen * 0.5, (Math.random() - 0.5) * 0.3, 0, Math.PI * 2);
        ctx.fill();
      }
      // Source spot at top
      const spotGrad = ctx.createRadialGradient(cx, 15, 0, cx, 15, 25);
      spotGrad.addColorStop(0, 'rgba(180,90,10,0.6)');
      spotGrad.addColorStop(1, 'rgba(150,70,5,0)');
      ctx.fillStyle = spotGrad;
      ctx.beginPath();
      ctx.arc(cx, 15, 25, 0, Math.PI * 2);
      ctx.fill();
      break;
    }

    default: {
      // Generic dark spot
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, cx);
      grad.addColorStop(0, 'rgba(40,35,30,0.5)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;

  // Release canvas bitmap memory — texture already uploaded to GPU.
  // Canvas is never appended to DOM, but zeroing dimensions frees the backing store.
  canvas.width = 0;
  canvas.height = 0;
  // Null the texture source so the canvas can be GC'd
  (tex.source as { data: HTMLCanvasElement | null }).data = null;

  return tex;
}

// ---------------------------------------------------------------------------
// Decal material cache
// ---------------------------------------------------------------------------

const decalMaterialCache = new Map<string, THREE.MeshBasicMaterial>();

/** Static (map-baked, non-pooled) decal meshes tracked for disposal on map change. */
const staticDecalMeshes: THREE.Mesh[] = [];

// NOTE: decalMaterialCache serves as a template cache — each decal gets a clone
// so it can be independently disposed without affecting other decals using the
// same type. The cache avoids re-creating the base material + texture each time.
// Each clone's lifetime is owned by its host mesh (ring buffer pool, static
// decal list, or permanent decal list); each of those disposes the material
// when the mesh is recycled or unloaded — no separate clone tracker is needed.

function getDecalMaterial(type: string): THREE.MeshBasicMaterial {
  let mat = decalMaterialCache.get(type);
  if (!mat) {
    mat = new THREE.MeshBasicMaterial({
      map: getDecalTexture(type),
      transparent: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      side: THREE.DoubleSide,
    });
    decalMaterialCache.set(type, mat);
  }

  // Clone from cache template for each decal instance.
  return mat.clone();
}

// ---------------------------------------------------------------------------
// Runtime decal spawning (ring buffer pool)
// ---------------------------------------------------------------------------

/**
 * Spawn a runtime decal (bullet impact, blood, scorch).
 * Uses a ring buffer pool — oldest decals are recycled.
 */
export function spawnDecal(
  position: THREE.Vector3,
  normal: THREE.Vector3,
  type: string,
  size = 0.15,
): void {
  if (!decalScene) return;

  // Find the mesh under this position for DecalGeometry projection
  // For simplicity, use a plane decal aligned to the normal
  const material = getDecalMaterial(type);
  const geometry = new THREE.PlaneGeometry(size, size);
  const mesh = new THREE.Mesh(geometry, material);

  mesh.position.copy(position).addScaledVector(normal, 0.005);

  // Orient to face along the normal.
  // F-MAP-013: For ground-projected decals (normal ≈ ±Y) the default mesh.up
  // (0,1,0) makes lookAt degenerate (forward parallel to up) and Three.js
  // falls back to an arbitrary roll, so decals randomly rotate frame-to-
  // frame. Pick a non-parallel up explicitly and copy it to mesh.up first.
  const up = Math.abs(normal.y) > 0.99
    ? new THREE.Vector3(0, 0, 1)
    : new THREE.Vector3(0, 1, 0);
  mesh.up.copy(up);
  const target = position.clone().add(normal);
  mesh.lookAt(target);

  // Add random rotation around the normal for variety
  mesh.rotateZ(Math.random() * Math.PI * 2);

  mesh.layers.set(EFFECT_LAYER); // keep off raycaster layer
  decalScene.add(mesh);

  // Ring buffer management
  if (decalPool.length >= MAX_DECALS) {
    const oldest = decalPool[decalIndex];
    if (oldest) {
      decalScene.remove(oldest.mesh);
      oldest.mesh.geometry.dispose();
      (oldest.mesh.material as THREE.Material).dispose();
    }
    decalPool[decalIndex] = { mesh, age: performance.now() };
  } else {
    decalPool.push({ mesh, age: performance.now() });
  }
  decalIndex = (decalIndex + 1) % MAX_DECALS;
}

/**
 * Spawn a decal projected onto a target mesh using DecalGeometry.
 * More accurate than plane decals but more expensive.
 */
export function spawnProjectedDecal(
  targetMesh: THREE.Mesh,
  position: THREE.Vector3,
  orientation: THREE.Euler,
  size: THREE.Vector3,
  type: string,
): void {
  if (!decalScene) return;

  const material = getDecalMaterial(type);

  try {
    const decalGeo = new DecalGeometry(targetMesh, position, orientation, size);
    const mesh = new THREE.Mesh(decalGeo, material);
    mesh.layers.set(EFFECT_LAYER);
    decalScene.add(mesh);

    // Ring buffer
    if (decalPool.length >= MAX_DECALS) {
      const oldest = decalPool[decalIndex];
      if (oldest) {
        decalScene.remove(oldest.mesh);
        oldest.mesh.geometry.dispose();
        (oldest.mesh.material as THREE.Material).dispose();
      }
      decalPool[decalIndex] = { mesh, age: performance.now() };
    } else {
      decalPool.push({ mesh, age: performance.now() });
    }
    decalIndex = (decalIndex + 1) % MAX_DECALS;
  } catch {
    // DecalGeometry can fail on edge cases — fall back silently
    material.dispose();
  }
}

// ---------------------------------------------------------------------------
// Static map decals (loaded from dust2.json)
// ---------------------------------------------------------------------------

/**
 * Load static decals defined in the map data.
 * These are permanent and not subject to the ring buffer pool.
 */
export function loadStaticDecals(
  scene: THREE.Scene,
  decals: DecalDef[],
): void {
  for (const decalDef of decals) {
    const material = getDecalMaterial(decalDef.type);
    const [sx, sy, sz] = decalDef.size ?? [0.5, 0.5, 0.01];
    const geometry = new THREE.PlaneGeometry(sx, sy);
    const mesh = new THREE.Mesh(geometry, material);

    mesh.position.set(...decalDef.position);
    if (decalDef.rotation) {
      mesh.rotation.set(decalDef.rotation[0], decalDef.rotation[1], decalDef.rotation[2]);
    } else {
      // Default: face +Z (wall-mounted)
      mesh.rotation.y = 0;
    }

    mesh.renderOrder = 1; // Render after geometry
    mesh.layers.set(EFFECT_LAYER); // keep off raycaster layer
    scene.add(mesh);
    staticDecalMeshes.push(mesh);
  }
}

// ---------------------------------------------------------------------------
// Permanent decal API (map-placed, non-pooled)
// ---------------------------------------------------------------------------

export interface PermanentDecalDef {
  position: [number, number, number];
  normal: [number, number, number];
  type: string;
  /** Width of the decal plane (default 1.0) */
  width?: number;
  /** Height of the decal plane (default 1.0) */
  height?: number;
  /** Optional Y rotation in radians */
  rotationY?: number;
}

/**
 * Add a single permanent (map-baked) decal to the scene.
 * These are never recycled by the ring buffer.
 */
export function addPermanentDecal(
  scene: THREE.Scene,
  def: PermanentDecalDef,
): void {
  const material = getDecalMaterial(def.type);
  const w = def.width ?? 1.0;
  const h = def.height ?? 1.0;
  const geometry = new THREE.PlaneGeometry(w, h);
  const mesh = new THREE.Mesh(geometry, material);

  mesh.position.set(...def.position);

  const normal = new THREE.Vector3(...def.normal).normalize();
  const offset = 0.005;
  mesh.position.addScaledVector(normal, offset);

  // F-MAP-013: see spawnDecal — copy `up` to mesh.up so lookAt for ground
  // decals (normal ≈ ±Y) doesn't pick an arbitrary roll.
  const up = Math.abs(normal.y) > 0.99
    ? new THREE.Vector3(0, 0, 1)
    : new THREE.Vector3(0, 1, 0);
  mesh.up.copy(up);
  const target = mesh.position.clone().add(normal);
  mesh.lookAt(target);

  if (def.rotationY !== undefined) {
    mesh.rotateZ(def.rotationY);
  }

  mesh.renderOrder = 1;
  mesh.layers.set(EFFECT_LAYER);
  scene.add(mesh);
  permanentDecals.push(mesh);
}

/**
 * Seed the scene with a set of static environmental permanent decals.
 * Call once after the map geometry is loaded.
 *
 * R-C-002: Idempotent — if `permanentDecals` already holds entries, this is
 * a no-op. `createScene()` on the GLB map path now calls this, and a map
 * switch / replay reload would otherwise duplicate every map-baked decal.
 */
export function initPermanentDecals(scene: THREE.Scene): void {
  if (permanentDecals.length > 0) return;

  // These are representative ambient decals for de_outpost-style maps.
  // Map designers can extend or override this list.
  const defs: PermanentDecalDef[] = [
    // Ground markings near bomb sites
    { position: [4.0, 0.01, 2.0], normal: [0, 1, 0], type: 'ground_marking', width: 1.4, height: 1.4 },
    { position: [-4.0, 0.01, 2.0], normal: [0, 1, 0], type: 'ground_marking', width: 1.4, height: 1.4, rotationY: Math.PI },
    // Parking stripes
    { position: [0, 0.01, 5.0], normal: [0, 1, 0], type: 'parking_stripe', width: 0.4, height: 3.0 },
    { position: [2.0, 0.01, 5.0], normal: [0, 1, 0], type: 'parking_stripe', width: 0.4, height: 3.0 },
    // Arrow markings
    { position: [0, 0.01, -3.0], normal: [0, 1, 0], type: 'arrow_marking', width: 1.2, height: 1.2 },
    // Oil stains (floor)
    { position: [3.0, 0.01, -1.0], normal: [0, 1, 0], type: 'oil_stain', width: 1.8, height: 1.2 },
    { position: [-2.5, 0.01, 3.5], normal: [0, 1, 0], type: 'oil_stain', width: 1.2, height: 0.9 },
    // Water stains (floor near walls)
    { position: [6.0, 0.01, 0], normal: [0, 1, 0], type: 'water_stain', width: 1.5, height: 1.5 },
    // Graffiti (walls — face +Z)
    { position: [-6.0, 1.5, 0.02], normal: [0, 0, 1], type: 'graffiti', width: 1.2, height: 0.9 },
    { position: [0, 1.8, -8.0], normal: [0, 0, 1], type: 'graffiti', width: 1.5, height: 1.1 },
    // Signs on walls
    { position: [1.5, 1.8, -8.02], normal: [0, 0, 1], type: 'sign', width: 0.9, height: 0.55 },
    // Wall cracks
    { position: [-1.0, 1.5, -8.02], normal: [0, 0, 1], type: 'wall_crack', width: 0.6, height: 1.2 },
    { position: [5.0, 2.0, 0.02], normal: [0, 0, 1], type: 'wall_crack', width: 0.5, height: 1.0 },
    // Moss patches (low wall areas)
    { position: [-8.0, 0.6, 0.02], normal: [0, 0, 1], type: 'moss_patch', width: 1.2, height: 0.8 },
    // Rust drips (walls with metal)
    { position: [8.0, 1.5, 0.02], normal: [0, 0, 1], type: 'rust_drip', width: 0.8, height: 1.5 },
    { position: [0, 1.5, 8.02], normal: [0, 0, -1], type: 'rust_drip', width: 0.7, height: 1.2 },
    // Stains on floor (existing type)
    { position: [-1.0, 0.01, 1.0], normal: [0, 1, 0], type: 'stain', width: 1.0, height: 1.0 },
    { position: [2.0, 0.01, -2.0], normal: [0, 1, 0], type: 'stain', width: 0.8, height: 0.8 },

    // ── Additional ground markings ──
    { position: [-8.0, 0.01, -3.0], normal: [0, 1, 0], type: 'ground_marking', width: 1.4, height: 1.4, rotationY: Math.PI / 2 },
    { position: [12.0, 0.01, 8.0], normal: [0, 1, 0], type: 'arrow_marking', width: 1.0, height: 1.0, rotationY: -Math.PI / 2 },
    { position: [-15.0, 0.01, 10.0], normal: [0, 1, 0], type: 'arrow_marking', width: 1.2, height: 1.2, rotationY: Math.PI },

    // ── Parking stripes (additional) ──
    { position: [4.0, 0.01, 5.0], normal: [0, 1, 0], type: 'parking_stripe', width: 0.4, height: 3.0 },
    { position: [-2.0, 0.01, 5.0], normal: [0, 1, 0], type: 'parking_stripe', width: 0.4, height: 3.0 },
    { position: [6.0, 0.01, 5.0], normal: [0, 1, 0], type: 'parking_stripe', width: 0.4, height: 3.0 },

    // ── More oil stains ──
    { position: [-5.0, 0.01, -5.0], normal: [0, 1, 0], type: 'oil_stain', width: 1.5, height: 1.0 },
    { position: [7.0, 0.01, 6.0], normal: [0, 1, 0], type: 'oil_stain', width: 1.0, height: 1.4 },
    { position: [10.0, 0.01, -4.0], normal: [0, 1, 0], type: 'oil_stain', width: 2.0, height: 1.5 },

    // ── Floor stains (water damage, general wear) ──
    { position: [-3.0, 0.01, 7.0], normal: [0, 1, 0], type: 'water_stain', width: 1.8, height: 1.8 },
    { position: [5.0, 0.01, -6.0], normal: [0, 1, 0], type: 'stain', width: 1.2, height: 1.2 },
    { position: [-7.0, 0.01, 4.0], normal: [0, 1, 0], type: 'stain', width: 0.9, height: 0.9 },
    { position: [8.0, 0.01, 3.0], normal: [0, 1, 0], type: 'water_stain', width: 1.3, height: 1.3 },

    // ── Wall graffiti (various walls) ──
    { position: [8.0, 1.5, -8.02], normal: [0, 0, 1], type: 'graffiti', width: 1.8, height: 1.0 },
    { position: [-4.0, 1.2, 8.02], normal: [0, 0, -1], type: 'graffiti', width: 1.3, height: 0.8 },
    { position: [8.02, 1.6, 3.0], normal: [-1, 0, 0], type: 'graffiti', width: 1.0, height: 0.7 },

    // ── Wall signs ──
    { position: [-8.02, 1.5, -2.0], normal: [1, 0, 0], type: 'sign', width: 0.7, height: 0.45 },
    { position: [3.0, 2.0, 8.02], normal: [0, 0, -1], type: 'sign', width: 0.8, height: 0.5 },

    // ── Wall cracks (structural damage) ──
    { position: [6.0, 1.0, -8.02], normal: [0, 0, 1], type: 'wall_crack', width: 0.7, height: 1.5 },
    { position: [-8.02, 2.0, 5.0], normal: [1, 0, 0], type: 'wall_crack', width: 0.5, height: 1.0 },
    { position: [8.02, 0.8, -3.0], normal: [-1, 0, 0], type: 'crack', width: 0.4, height: 0.8 },

    // ── Moss patches (shaded areas near walls) ──
    { position: [-6.0, 0.01, 3.0], normal: [0, 1, 0], type: 'moss_patch', width: 1.0, height: 0.6 },
    { position: [4.0, 0.3, -8.02], normal: [0, 0, 1], type: 'moss_patch', width: 0.8, height: 0.6 },

    // ── Rust drips (below metal fixtures, pipes) ──
    { position: [-3.0, 1.8, -8.02], normal: [0, 0, 1], type: 'rust_drip', width: 0.5, height: 1.0 },
    { position: [8.02, 1.2, 0], normal: [-1, 0, 0], type: 'rust_drip', width: 0.6, height: 1.3 },
    { position: [-8.02, 1.0, -5.0], normal: [1, 0, 0], type: 'rust_drip', width: 0.4, height: 0.9 },

    // ── Scorch marks (near bombsites) ──
    { position: [3.5, 0.01, 1.5], normal: [0, 1, 0], type: 'scorch', width: 1.5, height: 1.5 },
    { position: [-3.5, 0.01, 2.5], normal: [0, 1, 0], type: 'scorch', width: 1.2, height: 1.2 },
  ];

  for (const def of defs) {
    addPermanentDecal(scene, def);
  }
}

/**
 * Dispose all permanent (map-baked) decals. Call on map unload.
 */
export function disposePermanentDecals(scene: THREE.Scene): void {
  for (const mesh of permanentDecals) {
    scene.remove(mesh);
    mesh.geometry.dispose();
    (mesh.material as THREE.Material).dispose();
  }
  permanentDecals.length = 0;
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

export function disposeDecals(): void {
  for (const entry of decalPool) {
    if (entry && decalScene) {
      decalScene.remove(entry.mesh);
      entry.mesh.geometry.dispose();
      (entry.mesh.material as THREE.Material).dispose();
    }
  }
  decalPool.length = 0;
  decalIndex = 0;

  // Remove static decal meshes from the scene and free their geometry +
  // their per-instance cloned material.
  for (const mesh of staticDecalMeshes) {
    mesh.removeFromParent();
    mesh.geometry.dispose();
    const mat = mesh.material as THREE.Material | THREE.Material[];
    if (Array.isArray(mat)) {
      for (const m of mat) m.dispose();
    } else {
      mat.dispose();
    }
  }
  staticDecalMeshes.length = 0;

  decalTextureCache.forEach(t => t.dispose());
  decalTextureCache.clear();
  decalMaterialCache.forEach(m => { m.map?.dispose(); m.dispose(); });
  decalMaterialCache.clear();

  decalScene = null;
}
